import type { ProactiveGreetingMode } from '@teo/widget-shared';
import { logPopup } from '../popups/popup-logger.js';

const STORAGE_PREFIX = 'teo_proactive_reengage:';
const DWELL_TICK_MS = 15_000;

/**
 * Client gate for `first_dialog_reengage`:
 *
 * - **Active cycle** (new visitor, or after unlock): proactive + `/greeting` on
 *   every page until the visitor interacts with chat.
 * - **After chat interaction**: cycle closes; wait for visible dwell or return
 *   gap (`REENGAGE_SECONDS`), then unlock and restart the same every-page chain.
 */
export type ProactiveReengageState = {
  /** When true, allow timed popups on every page until next chat interaction. */
  cycleActive: boolean;
  /** Set when the visitor opens/engages the chat — closes the cycle. */
  lastInteractionAt: number | null;
  lastSessionEndAt: number | null;
  /** Visible dwell ms while waiting to re-open the cycle. */
  sessionDwellMs: number;
  sessionStartedAt: number | null;
  /** Telemetry only — does not gate frequency inside an active cycle. */
  lastShownAt: number | null;
};

export type ProactiveAllowDecision = {
  allow: boolean;
  reason: string;
};

type DwellTracker = {
  stop: () => void;
};

const dwellTrackers = new Map<string, DwellTracker>();

function canUseLocalStorage(): boolean {
  return typeof localStorage !== 'undefined';
}

function storageKey(slug: string): string {
  return `${STORAGE_PREFIX}${slug}`;
}

function emptyState(): ProactiveReengageState {
  return {
    cycleActive: false,
    lastInteractionAt: null,
    lastSessionEndAt: null,
    sessionDwellMs: 0,
    sessionStartedAt: null,
    lastShownAt: null,
  };
}

function readState(slug: string): ProactiveReengageState {
  if (!canUseLocalStorage()) {
    return emptyState();
  }
  try {
    const raw = localStorage.getItem(storageKey(slug));
    if (!raw) return emptyState();
    const parsed = JSON.parse(raw) as Partial<ProactiveReengageState>;
    return {
      cycleActive: parsed.cycleActive === true,
      lastInteractionAt:
        typeof parsed.lastInteractionAt === 'number'
          ? parsed.lastInteractionAt
          : null,
      lastSessionEndAt:
        typeof parsed.lastSessionEndAt === 'number'
          ? parsed.lastSessionEndAt
          : null,
      sessionDwellMs:
        typeof parsed.sessionDwellMs === 'number' && parsed.sessionDwellMs >= 0
          ? parsed.sessionDwellMs
          : 0,
      sessionStartedAt:
        typeof parsed.sessionStartedAt === 'number'
          ? parsed.sessionStartedAt
          : null,
      lastShownAt:
        typeof parsed.lastShownAt === 'number' ? parsed.lastShownAt : null,
    };
  } catch {
    return emptyState();
  }
}

function writeState(slug: string, state: ProactiveReengageState): void {
  if (!canUseLocalStorage()) return;
  try {
    localStorage.setItem(storageKey(slug), JSON.stringify(state));
  } catch {
    // ignore quota / private mode
  }
}

function gapMsFrom(reengageSeconds: number): number {
  return Math.max(60, reengageSeconds) * 1000;
}

/** Open a proactive cycle: every page until the next chat interaction. */
export function unlockProactiveCycle(slug: string): void {
  const state = readState(slug);
  writeState(slug, {
    ...state,
    cycleActive: true,
    sessionDwellMs: 0,
    // Consumed — require a fresh pagehide for the next return-gap unlock.
    lastSessionEndAt: null,
  });
  logPopup('reengage', 'cycle unlocked — every page until chat', { slug });
}

/**
 * Visitor engaged the chat — close the proactive cycle and start the suppress wait.
 */
export function markProactiveChatInteraction(
  slug: string,
  now = Date.now(),
): void {
  const state = readState(slug);
  writeState(slug, {
    ...state,
    cycleActive: false,
    lastInteractionAt: now,
    sessionDwellMs: 0,
    // Stale session-end must not immediately re-unlock after this interaction.
    lastSessionEndAt: null,
  });
  logPopup('reengage', 'chat interaction — cycle closed', { slug, at: now });
}

/**
 * Whether a proactive popup may be scheduled / shown for the current visit.
 *
 * `every_page` — legacy: always allow.
 * `first_dialog_reengage` — every page until chat interaction; after that wait
 * for return-gap / dwell, then unlock and repeat the every-page chain.
 */
export function shouldAllowProactivePopup(options: {
  mode: ProactiveGreetingMode;
  reengageSeconds: number;
  slug: string;
  now?: number;
}): ProactiveAllowDecision {
  const now = options.now ?? Date.now();
  if (options.mode === 'every_page') {
    return { allow: true, reason: 'mode_every_page' };
  }

  const state = readState(options.slug);

  // Never interacted, or cycle re-opened after re-engage → same as new visitor.
  if (state.lastInteractionAt == null || state.cycleActive) {
    return {
      allow: true,
      reason: state.cycleActive ? 'proactive_cycle' : 'first_visit',
    };
  }

  const gapMs = gapMsFrom(options.reengageSeconds);

  if (
    state.lastSessionEndAt != null &&
    now - state.lastSessionEndAt >= gapMs
  ) {
    unlockProactiveCycle(options.slug);
    return { allow: true, reason: 'return_after_gap' };
  }

  if (state.sessionDwellMs >= gapMs) {
    unlockProactiveCycle(options.slug);
    return { allow: true, reason: 'dwell_threshold' };
  }

  return { allow: false, reason: 'waiting_reengage' };
}

/** Telemetry when a proactive popup / auto-open is actually shown. */
export function markProactivePopupShown(slug: string, now = Date.now()): void {
  const state = readState(slug);
  writeState(slug, {
    ...state,
    lastShownAt: now,
  });
  logPopup('reengage', 'mark shown', { slug, at: now });
}

/** Start / continue a browser session. */
export function touchProactiveSession(slug: string, now = Date.now()): void {
  const state = readState(slug);
  if (state.sessionStartedAt == null) {
    writeState(slug, {
      ...state,
      sessionStartedAt: now,
    });
  }
}

/** Persist session end so the next visit can detect a return gap. */
export function markProactiveSessionEnd(slug: string, now = Date.now()): void {
  const state = readState(slug);
  writeState(slug, {
    ...state,
    lastSessionEndAt: now,
    sessionStartedAt: null,
    // Keep sessionDwellMs — MPA navigations fire pagehide; dwell must accumulate
    // across pages until chat interaction or unlock, not reset on every route.
  });
}

/**
 * While waiting after chat interaction, accumulate visible dwell. When the
 * threshold is reached, unlocks the cycle and invokes `onThreshold`.
 */
export function startProactiveDwellTracking(options: {
  slug: string;
  reengageSeconds: number;
  isPaused?: () => boolean;
  onThreshold: () => boolean | void;
}): () => void {
  const { slug, reengageSeconds, isPaused, onThreshold } = options;
  stopProactiveDwellTracking(slug);
  if (typeof window === 'undefined') {
    return () => undefined;
  }

  touchProactiveSession(slug);
  const gapMs = gapMsFrom(reengageSeconds);
  let lastTick = Date.now();

  const tick = () => {
    const state = readState(slug);
    // Active cycle or never interacted — no suppress timer to count.
    if (state.cycleActive || state.lastInteractionAt == null) {
      lastTick = Date.now();
      return;
    }
    if (isPaused?.()) {
      lastTick = Date.now();
      return;
    }
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      lastTick = Date.now();
      return;
    }
    const now = Date.now();
    const delta = Math.max(0, now - lastTick);
    lastTick = now;
    const nextDwell = state.sessionDwellMs + delta;
    writeState(slug, { ...state, sessionDwellMs: nextDwell });

    if (nextDwell >= gapMs) {
      const decision = shouldAllowProactivePopup({
        mode: 'first_dialog_reengage',
        reengageSeconds,
        slug,
        now,
      });
      if (decision.allow) {
        logPopup('reengage', 'dwell threshold reached', {
          slug,
          dwellMs: nextDwell,
          gapMs,
        });
        void onThreshold();
      }
    }
  };

  const intervalId = window.setInterval(tick, DWELL_TICK_MS);
  const onVisibility = () => {
    lastTick = Date.now();
  };
  const onPageHide = () => {
    markProactiveSessionEnd(slug);
  };

  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pagehide', onPageHide);

  const stop = () => {
    window.clearInterval(intervalId);
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('pagehide', onPageHide);
    dwellTrackers.delete(slug);
  };

  dwellTrackers.set(slug, { stop });
  return stop;
}

export function stopProactiveDwellTracking(slug: string): void {
  dwellTrackers.get(slug)?.stop();
}

export function clearProactiveReengageState(slug?: string): string[] {
  if (!canUseLocalStorage()) return [];
  const cleared: string[] = [];
  try {
    if (slug) {
      localStorage.removeItem(storageKey(slug));
      cleared.push(storageKey(slug));
      stopProactiveDwellTracking(slug);
      return cleared;
    }
    for (let i = localStorage.length - 1; i >= 0; i -= 1) {
      const key = localStorage.key(i);
      if (key?.startsWith(STORAGE_PREFIX)) {
        localStorage.removeItem(key);
        cleared.push(key);
      }
    }
  } catch {
    // ignore
  }
  return cleared;
}

export function getProactiveReengageState(slug: string): ProactiveReengageState {
  return readState(slug);
}
